<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <h5 class="title is-5">{{ $t("projects.create") }}</h5>
    <div class="content-box">
      <AlertMessage
        color="is-info"
        :message="$t('projects.createMessage')"
      ></AlertMessage>

      <AlertMessage color="is-danger" :message="message"></AlertMessage>
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        :initial-values="{ zoneinfo }"
        v-slot="{ errors }"
      >
        <div class="field">
          <label class="label">{{ $t("projects.projectId") }} *</label>
          <div class="control">
            <Field
              name="projectId"
              class="input"
              type="text"
              placeholder="ex) pj01"
              maxlength="10"
              required="required"
              :class="{ 'is-danger': errors.projectId }"
            />
            <span class="help is-danger">{{ errors.projectId }}</span>
            <p class="help is-info">{{ $t("projects.projectIdHelp") }}</p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("projects.projectName") }} *</label>
          <div class="control">
            <Field
              name="projectName"
              class="input"
              type="text"
              placeholder="ex) Your project name"
              maxlength="60"
              required="required"
              :class="{ 'is-danger': errors.projectName }"
            />
            <span class="help is-danger">{{ errors.projectName }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("Timezone") }}</label>
          <div class="select">
            <Field name="zoneinfo" as="select" class="form-control select-lang">
              <option value="9">Asia/Tokyo(JST)</option>
              <option value="-5">America/New_York(EST)</option>
            </Field>
          </div>
        </div>

        <div v-if="isSecondaryUse && plan == 'standard'">
          <div class="field">
            <label class="label">{{ $t("projects.secondaryUseTitle") }}</label>
            <label class="checkbox">
              <div class="control">
                <Field
                  name="secondaryUse"
                  type="checkbox"
                  unchecked-value="false"
                  value="true"
                />
                {{ $t("projects.secondaryUseMessage") }}
              </div>
            </label>
          </div>
          <div class="field">
            <a href="#" @click="showModal">{{
              $t("projects.secondaryUseLink")
            }}</a>
          </div>
        </div>

        <div v-if="isSecondaryUse && plan == 'agency'">
          <Field name="secondaryUse" type="hidden" value="true" />
        </div>

        <button
          type="submit"
          class="button is-block is-primary mt-4"
          v-bind:class="{ 'is-loading': isLoad }"
        >
          {{ $t("Create") }}
        </button>
      </Form>
    </div>
  </div>
  <Secondary
    :imageModalView="modalView"
    :company="company"
    @close="closeModal"
  />
</template>

<script>
import AlertMessage from "@/components/AlertMessage.vue";
import Breadcrumb from "@/components/Breadcrumb";
import Secondary from "@/components/projects/Secondary.vue";
import { ImsrProjects } from "@/lib/imsrProjects.js";
import { ImsrTenants } from "@/lib/imsrTenants.js";
import { ref } from "@vue/reactivity";
import { Field, Form } from "vee-validate";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  components: {
    AlertMessage,
    Breadcrumb,
    Secondary,
    Form,
    Field,
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const isLoad = ref(false);
    const message = ref();
    const breadcrumb = [{ title: "Create", path: "#", isActive: true }];
    const isSecondaryUse = ref(false);
    const plan = ref("standard");
    const modalView = ref(false);
    const company = ref();
    const zoneinfo = ref("9");

    const schema = Yup.object().shape({
      projectId: Yup.string()
        .required(t("validate.required"))
        .matches(/^[0-9a-zA-Z]*$/, t("validate.projectId.matches"))
        .min(3, t("validate.projectId.min")),
      projectName: Yup.string().required(t("validate.required")),
      secondaryUse: Yup.string(),
      zoneinfo: Yup.string().required(t("validate.required")),
    });

    const loadTenant = async () => {
      try {
        const imsrTenants = new ImsrTenants();
        const res = await imsrTenants.getTenant(store.getters.tenantId);
        company.value = res.company;
        plan.value = res.plan;
        if (res.secondaryUse == true) {
          isSecondaryUse.value = true;
        }
      } catch (e) {
        isSecondaryUse.value = false;
      }
    };
    loadTenant();

    const onSubmit = async (values) => {
      try {
        isLoad.value = true;
        const imsrProjects = new ImsrProjects();
        await imsrProjects.createProject({
          tenantId: store.getters.tenantId,
          projectId: values.projectId,
          projectName: values.projectName,
          secondaryUse: values.secondaryUse,
          zoneinfo: values.zoneinfo.toString(),
        });
        store.commit("setProjectId", values.projectId);
        store.commit("setProjectName", values.projectName);
        router.push({
          name: "projectsView",
          params: { projectId: values.projectId },
        });
      } catch (e) {
        if (e.message == "projectId exists.") {
          message.value = t("projects.projectExist");
        } else if (e.message == "Invalid projectId.") {
          message.value = t("projects.invalidProject");
        } else {
          router.push({ name: "InternalServerError" });
        }
      }
      isLoad.value = false;
    };

    const showModal = () => {
      modalView.value = true;
    };

    const closeModal = () => {
      modalView.value = false;
    };

    return {
      breadcrumb,
      schema,
      onSubmit,
      isLoad,
      message,
      isSecondaryUse,
      showModal,
      closeModal,
      modalView,
      company,
      plan,
      zoneinfo,
    };
  },
};
</script>
